import { getLogger } from '@sas/nova-core/logging';
import { useState, useEffect, useRef, useCallback } from 'react';
import { AvatarProps } from '..';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { AvatarDataProvider } from '../';
import bundle from '../NovaComponents-Avatar-gui-icu.properties';
import { useOnAvatarUpdate, useUserGroupActualId } from './useOnAvatarUpdate';

const useTextLocalizer = createUseTextLocalizer(bundle);

const logger = getLogger('Avatar');

export const useAvatarA11yInfo = (
  props: Pick<AvatarProps, 'ariaLabel' | 'tooltip' | 'type' | 'userId' | 'useAsButton'> & {
    dataProvider: AvatarDataProvider;
  }
): {
  ariaLabel?: string;
  tooltip?: string;
  name?: string | null | undefined;
} => {
  const localize = useTextLocalizer();
  const [name, setName] = useState<string | null | undefined>(undefined);
  const actualId = useUserGroupActualId(props.dataProvider, props.userId, props.type);
  const [fetchedTime, setFetchedTime] = useState<number>();
  const fetchCounter = useRef(0);

  const defaultName = actualId === props.userId ? props.userId : null;

  useEffect(() => {
    return () => {
      // Increment the fetchCounter to discard the fetch response after unmounting
      fetchCounter.current += 1;
    };
  }, []);

  const updateName = useCallback(async () => {
    const fetchCount = ++fetchCounter.current;
    try {
      const name = await props.dataProvider.getName(props.userId, props.type);
      if (fetchCounter.current > fetchCount) return;
      setName(name.length > 0 ? name : defaultName);
    } catch (e) {
      if (fetchCounter.current > fetchCount) return;
      setName(null);
      logger.error(`Failed to fetch the user info for id:${props.userId} and type: ${props.type}`);
    } finally {
      if (fetchCounter.current > fetchCount) return;
      setFetchedTime(Date.now());
    }
  }, [props.dataProvider, defaultName, props.userId, props.type]);

  useOnAvatarUpdate(props.dataProvider, updateName, props.userId, props.type, fetchedTime);

  useEffect(() => {
    updateName();
  }, [updateName]);

  let defaultTooltip = '';
  if (name === undefined) {
    defaultTooltip = localize('loading.avatar.tooltip.label');
  } else if (name === null) {
    defaultTooltip = localize(`unknown.${props.type ?? 'user'}.tooltip.label`);
  } else {
    defaultTooltip = localize(`avatar.${props.type ?? 'user'}.tooltip.label`, { name });
  }

  const tooltip =
    props.tooltip === ''
      ? props.useAsButton // According to a11y guidelines, button should always have a aria-label
        ? defaultTooltip
        : undefined
      : props.tooltip ?? defaultTooltip;
  const ariaLabel = props.ariaLabel === '' ? tooltip : props.ariaLabel ?? tooltip ?? defaultTooltip;

  return { ariaLabel, tooltip, name };
};
